import { useState, useEffect, useRef, useMemo } from 'react';
import { createRoute, Link } from '@tanstack/react-router';
import { Shell } from '../components/layout/Shell';
import { useAuth } from '../hooks/useAuth';
import { useTranslation } from '../i18n';
import { useAppMode } from '../context/AppModeContext';
import { Route as rootRoute } from './__root';
import type { TranslationKey } from '../i18n';
import styles from './guide.module.css';

export const Route = createRoute({
  getParentRoute: () => rootRoute,
  path: '/guide',
  component: GuidePage,
});

// ---------- TOC scroll tracking ----------

function useActiveSection(sectionIds: string[]) {
  const [activeId, setActiveId] = useState(sectionIds[0] ?? '');
  const observerRef = useRef<IntersectionObserver | null>(null);

  useEffect(() => {
    observerRef.current?.disconnect();

    const entries = new Map<string, IntersectionObserverEntry>();

    observerRef.current = new IntersectionObserver(
      (observed) => {
        for (const entry of observed) {
          entries.set(entry.target.id, entry);
        }
        // Pick the first visible section (top-most)
        for (const id of sectionIds) {
          const e = entries.get(id);
          if (e?.isIntersecting) {
            setActiveId(id);
            return;
          }
        }
      },
      { rootMargin: '-80px 0px -60% 0px', threshold: 0 },
    );

    for (const id of sectionIds) {
      const el = document.getElementById(id);
      if (el) observerRef.current.observe(el);
    }

    return () => observerRef.current?.disconnect();
  }, [sectionIds]);

  return activeId;
}

type TocSection = { id: string; label: string; group: string };

function TableOfContents({
  sections,
  activeId,
}: {
  sections: TocSection[];
  activeId: string;
}) {
  const { t } = useTranslation();

  const groups: Array<{ label: string; items: TocSection[] }> = [];
  for (const section of sections) {
    const last = groups[groups.length - 1];
    if (last && last.label === section.group) {
      last.items.push(section);
    } else {
      groups.push({ label: section.group, items: [section] });
    }
  }

  return (
    <nav className={styles.toc} aria-label="Table of contents">
      <div className={styles.tocHeading}>{t('guide.onThisPage')}</div>
      <ul className={styles.tocList}>
        {groups.map((group) => (
          <li key={group.label} className={styles.tocGroup}>
            <div className={styles.tocGroupLabel}>{group.label}</div>
            <ul className={styles.tocGroupItems}>
              {group.items.map(({ id, label }) => (
                <li key={id}>
                  <a
                    href={`#${id}`}
                    className={`${styles.tocLink} ${activeId === id ? styles.tocLinkActive : ''}`}
                    onClick={(e) => {
                      e.preventDefault();
                      document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }}
                  >
                    {label}
                  </a>
                </li>
              ))}
            </ul>
          </li>
        ))}
      </ul>
    </nav>
  );
}

// ---------- Main ----------

function GuidePage() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const { mode } = useAppMode();
  const isAdmin = user?.role === 'admin';

  // Build section list (some are conditional)
  const sections = useMemo(() => {
    const gStart = t('guide.groupGettingStarted');
    const gColl = t('guide.groupCollection');
    const gItem = t('guide.groupItemFeatures');
    const gOrg = t('guide.groupOrganization');
    const gComm = t('guide.groupCommunity');
    const gAcct = t('guide.groupAccount');

    const s: TocSection[] = [
      // Getting Started
      { id: 'web-vs-desktop', label: t('guide.tocWebVsDesktop' as TranslationKey) || 'Web vs Desktop', group: gStart },
      { id: 'getting-started', label: t('guide.gettingStarted'), group: gStart },
      { id: 'roles', label: t('guide.rolesPermissions'), group: gStart },
      // Your Collection
      { id: 'inventory', label: t('guide.inventoryManagement'), group: gColl },
      { id: 'browsing', label: t('guide.browsingFiltering'), group: gColl },
      { id: 'image-editor', label: t('guide.imageEditor'), group: gColl },
      { id: 'batch-add', label: t('guide.batchAdd'), group: gColl },
      { id: 'ai-analysis', label: t('guide.aiImageAnalysis'), group: gColl },
      // Item Features
      { id: 'peripherals', label: t('guide.peripheralLinks'), group: gItem },
      { id: 'recap', label: t('guide.recapStatusShort'), group: gItem },
      { id: 'retrobright', label: t('guide.retrobrightTracker'), group: gItem },
      { id: 'health', label: t('guide.maintenancePulseShort'), group: gItem },
      { id: 'library', label: t('guide.theLibrary'), group: gItem },
      // Organization & Insights
      { id: 'multi-vault', label: t('guide.multiVault'), group: gOrg },
      { id: 'duplicates', label: t('guide.duplicateDetection'), group: gOrg },
      { id: 'sets', label: t('guide.setCompleteness'), group: gOrg },
      { id: 'stats', label: t('guide.statsDashboard'), group: gOrg },
      { id: 'export', label: t('guide.export'), group: gOrg },
      // Community
      { id: 'community', label: t('guide.community'), group: gComm },
      { id: 'messaging', label: t('guide.directMessaging'), group: gComm },
      { id: 'sharing', label: t('guide.catalogSharing'), group: gComm },
      { id: 'favorites', label: t('guide.favorites'), group: gComm },
      { id: 'activity', label: t('guide.activityFeed'), group: gComm },
      { id: 'notifications', label: t('guide.notifications'), group: gComm },
      // Account & Tips
    ];
    if (mode !== 'desktop') s.push({ id: 'plans', label: t('guide.plans'), group: gAcct });
    if (isAdmin) s.push({ id: 'admin-panel', label: t('guide.adminPanel'), group: gAcct });
    s.push({ id: 'tips', label: t('guide.tips'), group: gAcct });
    return s;
  }, [t, isAdmin, mode]);

  const sectionIds = useMemo(() => sections.map((s) => s.id), [sections]);
  const activeId = useActiveSection(sectionIds);

  return (
    <Shell>
      <div className={styles.page}>
        <h1 className={styles.title}>{t('guide.title')}</h1>
        <p className={styles.subtitle}>{t('guide.subtitle')}</p>

        <div className={styles.layout}>
          <div className={styles.content}>

            {/* ===== GETTING STARTED ===== */}

            {/* Web vs Desktop */}
            <div id="web-vs-desktop" className={styles.section}>
              <h2 className={styles.sectionTitle}>Web App vs Desktop App</h2>
              <p className={styles.subtitle} style={{ marginBottom: '1rem' }}>
                SiliconVault is available as a hosted web application and as a native offline desktop app.
                Both share the same core inventory engine — the key differences are around community features, data storage, and the access model.
              </p>
              <div>
                <div className={styles.card}>
                  <div className={styles.cardTitle}>
                    Web App
                    {mode === 'web' && <span className={styles.roleBadge}>current</span>}
                  </div>
                  <ul className={styles.list}>
                    <li><strong>Multi-user</strong> — admin and user roles, invite-only or open registration</li>
                    <li><strong>Community features</strong> — Showroom, Wanted List, Offers &amp; Trading, Favorites, Notifications, Public Profiles</li>
                    <li><strong>Authentication</strong> — email &amp; password, or Google OAuth (accounts linked automatically)</li>
                    <li><strong>Cloud storage</strong> — data backed by PostgreSQL; images stored on Vercel Blob</li>
                    <li><strong>AI image analysis</strong> — bring your own API key or upgrade to Pro for hosted analysis</li>
                    <li><strong>No installation required</strong> — runs in any modern browser on any OS</li>
                    <li><strong>Accessible on mobile</strong> — responsive layout works on phones and tablets</li>
                  </ul>
                  <hr className={styles.divider} />
                  <p style={{ fontSize: '0.8125rem', color: 'var(--text-muted)', margin: 0, lineHeight: 1.6 }}>
                    Community features are exclusive to the web app and are not available in the desktop version.
                  </p>
                </div>

                <div className={styles.card}>
                  <div className={styles.cardTitle}>
                    Desktop App
                    {mode === 'desktop' && <span className={styles.roleBadge}>current</span>}
                  </div>
                  <ul className={styles.list}>
                    <li><strong>Single-user</strong> — no registration, no server, works out of the box</li>
                    <li>
                      <strong>Fully offline</strong> — all data stored locally on your device:
                      <ul className={styles.list} style={{ marginTop: '0.375rem' }}>
                        <li><strong>macOS</strong> — <code style={{ fontFamily: 'var(--font-mono)', fontSize: '0.8125rem' }}>~/Library/Application Support/app.siliconvault/</code></li>
                        <li><strong>Windows</strong> — <code style={{ fontFamily: 'var(--font-mono)', fontSize: '0.8125rem' }}>%APPDATA%\app.siliconvault\</code></li>
                        <li><strong>Linux</strong> — <code style={{ fontFamily: 'var(--font-mono)', fontSize: '0.8125rem' }}>~/.local/share/app.siliconvault/</code></li>
                      </ul>
                      The database file is <code style={{ fontFamily: 'var(--font-mono)', fontSize: '0.8125rem' }}>siliconvault.db</code>; uploaded images are in the <code style={{ fontFamily: 'var(--font-mono)', fontSize: '0.8125rem' }}>uploads/</code> subfolder.
                    </li>
                    <li><strong>One-time license</strong> — permanent access, no subscription</li>
                    <li><strong>AI image analysis</strong> — configure your own API key per-device in Settings</li>
                    <li><strong>Local image storage</strong> — photos saved to your app data directory</li>
                    <li><strong>Export</strong> — CSV, JSON, and PDF catalog export fully supported</li>
                    <li><strong>No community features</strong> — Showroom, Offers, Wanted List, and Notifications are not available</li>
                  </ul>
                  <hr className={styles.divider} />
                  <div style={{ fontSize: '0.8125rem', fontWeight: 600, color: 'var(--accent)', marginBottom: '0.5rem' }}>
                    Platform Support
                  </div>
                  <ul className={styles.list}>
                    <li><strong>macOS</strong> — Universal binary (Apple Silicon M1+ and Intel), macOS 11 Big Sur and later</li>
                    <li><strong>Windows</strong> — Windows 10 and Windows 11, x64</li>
                    <li><strong>Linux</strong> — AppImage and .deb, x64; tested on Ubuntu 20.04+, Fedora 38+, Debian 11+</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Getting Started */}
            <div id="getting-started" className={styles.section}>
              <h2 className={styles.sectionTitle}>{t('guide.gettingStarted')}</h2>
              <ol className={styles.steps}>
                <li>
                  <span className={styles.stepNumber}>1</span>
                  <span><strong>{t('guide.step1Title')}</strong>{t('guide.step1Desc')}</span>
                </li>
                <li>
                  <span className={styles.stepNumber}>2</span>
                  <span><strong>{t('guide.step2Title')}</strong>{t('guide.step2Desc')}</span>
                </li>
                <li>
                  <span className={styles.stepNumber}>3</span>
                  <span><strong>{t('guide.step3Title')}</strong>{t('guide.step3Desc')}</span>
                </li>
                <li>
                  <span className={styles.stepNumber}>4</span>
                  <span><strong>{t('guide.step4Title')}</strong>{t('guide.step4Desc')}</span>
                </li>
              </ol>
            </div>

            {/* Roles & Permissions */}
            <div id="roles" className={styles.section}>
              <h2 className={styles.sectionTitle}>{t('guide.rolesPermissions')}</h2>
              <div className={styles.roleGrid}>
                {isAdmin && (
                  <div className={styles.card}>
                    <div className={styles.cardTitle}>
                      {t('guide.adminRole')} <span className={styles.roleBadge}>admin</span>
                    </div>
                    <ul className={styles.list}>
                      <li>{t('guide.adminPerm1')}</li>
                      <li>{t('guide.adminPerm2')}</li>
                      <li>{t('guide.adminPerm3')}</li>
                      <li>{t('guide.adminPerm5')}</li>
                      <li>{t('guide.adminPerm6')}</li>
                    </ul>
                  </div>
                )}
                <div className={styles.card}>
                  <div className={styles.cardTitle}>
                    {t('guide.userRole')} <span className={styles.roleBadge}>user</span>
                  </div>
                  <ul className={styles.list}>
                    <li>{t('guide.userPerm1')}</li>
                    <li>{t('guide.userPerm2')}</li>
                    <li>{t('guide.userPerm3')}</li>
                    <li>{t('guide.userPerm4')}</li>
                    <li>{t('guide.userPerm5')}</li>
                    <li>{t('guide.userPerm6')}</li>
                    <li>{t('guide.userPerm7')}</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* ===== YOUR COLLECTION ===== */}

            {/* Inventory Management */}
            <div id="inventory" className={styles.section}>
              <h2 className={styles.sectionTitle}>{t('guide.inventoryManagement')}</h2>
              <div className={styles.cardGrid}>
                <div className={styles.card}>
                  <div className={styles.cardTitle}>{t('guide.addingItemsTitle')}</div>
                  <p className={styles.cardText}>{t('guide.addingItemsDesc')}</p>
                </div>
                <div className={styles.card}>
                  <div className={styles.cardTitle}>{t('guide.categoriesTitle')}</div>
                  <p className={styles.cardText}>{t('guide.categoriesDesc')}</p>
                </div>
                <div className={styles.card}>
                  <div className={styles.cardTitle}>{t('guide.platformsTitle')}</div>
                  <p className={styles.cardText}>{t('guide.platformsDesc')}</p>
                </div>
                <div className={styles.card}>
                  <div className={styles.cardTitle}>{t('guide.conditionsTitle')}</div>
                  <ul className={styles.list}>
                    <li><strong>{t('guide.condMint')}</strong>{t('guide.condMintDesc')}</li>
                    <li><strong>{t('guide.condExcellent')}</strong>{t('guide.condExcellentDesc')}</li>
                    <li><strong>{t('guide.condGood')}</strong>{t('guide.condGoodDesc')}</li>
                    <li><strong>{t('guide.condFair')}</strong>{t('guide.condFairDesc')}</li>
                    <li><strong>{t('guide.condPoor')}</strong>{t('guide.condPoorDesc')}</li>
                    <li><strong>{t('guide.condParts')}</strong>{t('guide.condPartsDesc')}</li>
                  </ul>
                </div>
                <div className={styles.card}>
                  <div className={styles.cardTitle}>{t('guide.currencyTitle')}</div>
                  <p className={styles.cardText}>{t('guide.currencyDesc')}</p>
                </div>
                <div className={styles.card}>
                  <div className={styles.cardTitle}>{t('guide.customFieldsTitle')}</div>
                  <p className={styles.cardText}>{t('guide.customFieldsDesc')}</p>
                </div>
              </div>
            </div>

            {/* Browsing & Filtering */}
            <div id="browsing" className={styles.section}>
              <h2 className={styles.sectionTitle}>{t('guide.browsingFiltering')}</h2>
              <div className={styles.cardGrid}>
                <div className={styles.card}>
                  <div className={styles.cardTitle}>{t('guide.searchFiltersTitle')}</div>
                  <p className={styles.cardText}>{t('guide.searchFiltersDesc')}</p>
                </div>
                <div className={styles.card}>
                  <div className={styles.cardTitle}>{t('guide.viewsSortingTitle')}</div>
                  <p className={styles.cardText}>{t('guide.viewsSortingDesc')}</p>
                </div>
                <div className={styles.card}>
                  <div className={styles.cardTitle}>{t('guide.paginationTitle')}</div>
                  <p className={styles.cardText}>{t('guide.paginationDesc')}</p>
                </div>
              </div>
            </div>

            {/* Image Editor */}
            <div id="image-editor" className={styles.section}>
              <h2 className={styles.sectionTitle}>{t('guide.imageEditor')}</h2>
              <div className={styles.card}>
                <p className={styles.cardText}>{t('guide.imageEditorDesc')}</p>
                <hr className={styles.divider} />
                <ul className={styles.list}>
                  <li><strong>{t('guide.editorCrop')}</strong>{t('guide.editorCropDesc')}</li>
                  <li><strong>{t('guide.editorZoom')}</strong>{t('guide.editorZoomDesc')}</li>
                  <li><strong>{t('guide.editorRotate')}</strong>{t('guide.editorRotateDesc')}</li>
                  <li><strong>{t('guide.editorFlip')}</strong>{t('guide.editorFlipDesc')}</li>
                  <li><strong>{t('guide.editorReset')}</strong>{t('guide.editorResetDesc')}</li>
                </ul>
              </div>
            </div>

            {/* Batch Add */}
            <div id="batch-add" className={styles.section}>
              <h2 className={styles.sectionTitle}>{t('guide.batchAdd')}</h2>
              <div className={styles.card}>
                <p className={styles.cardText}>{t('guide.batchAddDesc')}</p>
                <hr className={styles.divider} />
                <ul className={styles.list}>
                  <li><strong>{t('guide.batchDrop')}</strong>{t('guide.batchDropDesc')}</li>
                  <li><strong>{t('guide.batchAi')}</strong>{t('guide.batchAiDesc')}</li>
                  <li><strong>{t('guide.batchReview')}</strong>{t('guide.batchReviewDesc')}</li>
                  <li><strong>{t('guide.batchProgress')}</strong>{t('guide.batchProgressDesc')}</li>
                  <li><strong>{t('guide.batchRetry')}</strong>{t('guide.batchRetryDesc')}</li>
                </ul>
              </div>
            </div>

            {/* AI Image Analysis */}
            <div id="ai-analysis" className={styles.section}>
              <h2 className={styles.sectionTitle}>{t('guide.aiImageAnalysis')}</h2>
              <div className={styles.card}>
                <p className={styles.cardText}>{t('guide.aiImageAnalysisDesc')}</p>
                <hr className={styles.divider} />
                <div className={styles.cardTitle}>{t('guide.aiByokTitle')}</div>
                <p className={styles.cardText}>{t('guide.aiByokDesc')}</p>
                <ul className={styles.list}>
                  <li><strong>{t('guide.aiProviderOpenai')}</strong>{t('guide.aiProviderOpenaiDesc')}</li>
                  <li><strong>{t('guide.aiProviderAzure')}</strong>{t('guide.aiProviderAzureDesc')}</li>
                  <li><strong>{t('guide.aiProviderGemini')}</strong>{t('guide.aiProviderGeminiDesc')}</li>
                  <li><strong>{t('guide.aiProviderCustom')}</strong>{t('guide.aiProviderCustomDesc')}</li>
                </ul>
                <hr className={styles.divider} />
                <div className={styles.cardTitle}>{t('guide.aiProTitle')}</div>
                <p className={styles.cardText}>{t('guide.aiProDesc')}</p>
                <hr className={styles.divider} />
                <p className={styles.cardText} style={{ fontSize: '0.8125rem', color: 'var(--text-muted)' }}>
                  {t('guide.aiCostNote')}
                </p>
              </div>
            </div>

            {/* ===== ITEM FEATURES ===== */}

            {/* Peripheral Links */}
            <div id="peripherals" className={styles.section}>
              <h2 className={styles.sectionTitle}>{t('guide.peripheralLinks')}</h2>
              <div className={styles.card}>
                <p className={styles.cardText}>{t('guide.peripheralLinksDesc')}</p>
                <hr className={styles.divider} />
                <ul className={styles.list}>
                  <li><strong>{t('guide.linkTypes')}</strong>{t('guide.linkTypesDesc')}</li>
                  <li><strong>{t('guide.linkBidirectional')}</strong>{t('guide.linkBidirectionalDesc')}</li>
                  <li><strong>{t('guide.linkDeleteWarnings')}</strong>{t('guide.linkDeleteWarningsDesc')}</li>
                  <li><strong>{t('guide.linkPlugIn')}</strong>{t('guide.linkPlugInDesc')}</li>
                </ul>
              </div>
            </div>

            {/* Recap Status */}
            <div id="recap" className={styles.section}>
              <h2 className={styles.sectionTitle}>{t('guide.recapStatus')}</h2>
              <div className={styles.card}>
                <p className={styles.cardText}>{t('guide.recapDesc')}</p>
                <hr className={styles.divider} />
                <ul className={styles.list}>
                  <li><strong>{t('guide.recapStatuses')}</strong>{t('guide.recapStatusesDesc')}</li>
                  <li><strong>{t('guide.recapProgress')}</strong>{t('guide.recapProgressDesc')}</li>
                  <li><strong>{t('guide.recapSchematic')}</strong>{t('guide.recapSchematicDesc')}</li>
                </ul>
              </div>
            </div>

            {/* Retrobright */}
            <div id="retrobright" className={styles.section}>
              <h2 className={styles.sectionTitle}>{t('guide.retrobrightTracker')}</h2>
              <div className={styles.card}>
                <p className={styles.cardText}>{t('guide.retrobrightDesc')}</p>
                <hr className={styles.divider} />
                <ul className={styles.list}>
                  <li><strong>{t('guide.retroProjectStatuses')}</strong>{t('guide.retroProjectStatusesDesc')}</li>
                  <li><strong>{t('guide.retroSessionTimer')}</strong>{t('guide.retroSessionTimerDesc')}</li>
                  <li><strong>{t('guide.retroPhotos')}</strong>{t('guide.retroPhotosDesc')}</li>
                  <li><strong>{t('guide.retroGlow')}</strong>{t('guide.retroGlowDesc')}</li>
                </ul>
              </div>
            </div>

            {/* Maintenance Pulse */}
            <div id="health" className={styles.section}>
              <h2 className={styles.sectionTitle}>{t('guide.maintenancePulse')}</h2>
              <div className={styles.card}>
                <p className={styles.cardText}>{t('guide.maintenancePulseDesc')}</p>
                <hr className={styles.divider} />
                <ul className={styles.list}>
                  <li><strong>{t('guide.healthStats')}</strong>{t('guide.healthStatsDesc')}</li>
                  <li><strong>{t('guide.healthCondition')}</strong>{t('guide.healthConditionDesc')}</li>
                  <li><strong>{t('guide.healthUrgent')}</strong>{t('guide.healthUrgentDesc')}</li>
                  <li><strong>{t('guide.healthAutoAlerts')}</strong>{t('guide.healthAutoAlertsDesc')}</li>
                  <li><strong>{t('guide.healthSeverities')}</strong>{t('guide.healthSeveritiesDesc')}</li>
                </ul>
              </div>
            </div>

            {/* Library */}
            <div id="library" className={styles.section}>
              <h2 className={styles.sectionTitle}>{t('guide.theLibrary')}</h2>
              <div className={styles.card}>
                <p className={styles.cardText}>{t('guide.libraryDesc')}</p>
                <hr className={styles.divider} />
                <ul className={styles.list}>
                  <li><strong>{t('guide.fileTypes')}</strong>{t('guide.fileTypesDesc')}</li>
                  <li><strong>{t('guide.validation')}</strong>{t('guide.validationDesc')}</li>
                  <li><strong>{t('guide.sha256')}</strong>{t('guide.sha256Desc')}</li>
                  <li><strong>{t('guide.filtering')}</strong>{t('guide.filteringDesc')}</li>
                  <li><strong>{t('guide.itemAssociation')}</strong>{t('guide.itemAssociationDesc')}</li>
                </ul>
              </div>
            </div>

            {/* ===== ORGANIZATION & INSIGHTS ===== */}

            {/* Multi-Vault */}
            <div id="multi-vault" className={styles.section}>
              <h2 className={styles.sectionTitle}>{t('guide.multiVault')}</h2>
              <div className={styles.card}>
                <p className={styles.cardText}>{t('guide.multiVaultDesc')}</p>
                <hr className={styles.divider} />
                <ul className={styles.list}>
                  <li><strong>{t('guide.vaultTypes')}</strong>{t('guide.vaultTypesDesc')}</li>
                  <li><strong>{t('guide.vaultSwitcher')}</strong>{t('guide.vaultSwitcherDesc')}</li>
                  <li><strong>{t('guide.vaultCreate')}</strong>{t('guide.vaultCreateDesc')}</li>
                  <li><strong>{t('guide.vaultScoping')}</strong>{t('guide.vaultScopingDesc')}</li>
                </ul>
              </div>
            </div>

            {/* Duplicate Detection */}
            <div id="duplicates" className={styles.section}>
              <h2 className={styles.sectionTitle}>{t('guide.duplicateDetection')}</h2>
              <div className={styles.card}>
                <p className={styles.cardText}>{t('guide.duplicateDetectionDesc')}</p>
                <hr className={styles.divider} />
                <ul className={styles.list}>
                  <li><strong>{t('guide.dupScan')}</strong>{t('guide.dupScanDesc')}</li>
                  <li><strong>{t('guide.dupReview')}</strong>{t('guide.dupReviewDesc')}</li>
                  <li><strong>{t('guide.dupDismiss')}</strong>{t('guide.dupDismissDesc')}</li>
                  <li><strong>{t('guide.dupMerge')}</strong>{t('guide.dupMergeDesc')}</li>
                </ul>
              </div>
            </div>

            {/* Set Completeness */}
            <div id="sets" className={styles.section}>
              <h2 className={styles.sectionTitle}>{t('guide.setCompleteness')}</h2>
              <div className={styles.card}>
                <p className={styles.cardText}>{t('guide.setCompletenessDesc')}</p>
                <hr className={styles.divider} />
                <ul className={styles.list}>
                  <li><strong>{t('guide.setTemplates')}</strong>{t('guide.setTemplatesDesc')}</li>
                  <li><strong>{t('guide.setCustom')}</strong>{t('guide.setCustomDesc')}</li>
                  <li><strong>{t('guide.setProgress')}</strong>{t('guide.setProgressDesc')}</li>
                  <li><strong>{t('guide.setAutoMatch')}</strong>{t('guide.setAutoMatchDesc')}</li>
                  <li><strong>{t('guide.setManualToggle')}</strong>{t('guide.setManualToggleDesc')}</li>
                </ul>
              </div>
            </div>

            {/* Stats Dashboard */}
            <div id="stats" className={styles.section}>
              <h2 className={styles.sectionTitle}>{t('guide.statsDashboard')}</h2>
              <div className={styles.card}>
                <p className={styles.cardText}>{t('guide.statsDashboardDesc')}</p>
                <hr className={styles.divider} />
                <ul className={styles.list}>
                  <li><strong>{t('guide.statsSummary')}</strong>{t('guide.statsSummaryDesc')}</li>
                  <li><strong>{t('guide.statsPlatformChart')}</strong>{t('guide.statsPlatformChartDesc')}</li>
                  <li><strong>{t('guide.statsConditionChart')}</strong>{t('guide.statsConditionChartDesc')}</li>
                  <li><strong>{t('guide.statsTimelineChart')}</strong>{t('guide.statsTimelineChartDesc')}</li>
                  <li><strong>{t('guide.statsValueChart')}</strong>{t('guide.statsValueChartDesc')}</li>
                </ul>
              </div>
            </div>

            {/* Data Backup & Export */}
            <div id="export" className={styles.section}>
              <h2 className={styles.sectionTitle}>{t('guide.export')}</h2>
              <div className={styles.card}>
                <p className={styles.cardText}>{t('guide.exportDesc')}</p>

                <hr className={styles.divider} />
                <div className={styles.cardTitle}>{t('guide.exportFormatsTitle')}</div>
                <ul className={styles.list}>
                  <li><strong>{t('guide.exportCsv')}</strong>{t('guide.exportCsvDesc')}</li>
                  <li><strong>{t('guide.exportJson')}</strong>{t('guide.exportJsonDesc')}</li>
                  <li><strong>{t('guide.exportPdf')}</strong>{t('guide.exportPdfDesc')}</li>
                  <li><strong>{t('guide.exportFilters')}</strong>{t('guide.exportFiltersDesc')}</li>
                </ul>

                <hr className={styles.divider} />
                <div className={styles.cardTitle}>{t('guide.exportDesktopTitle')}</div>
                <p className={styles.cardText}>{t('guide.exportDesktopDesc')}</p>
                <ul className={styles.list}>
                  <li><strong>{t('guide.exportDesktopAuto')}</strong>{t('guide.exportDesktopAutoDesc')}</li>
                  <li><strong>{t('guide.exportDesktopManual')}</strong>{t('guide.exportDesktopManualDesc')}</li>
                  <li><strong>{t('guide.exportDesktopRestore')}</strong>{t('guide.exportDesktopRestoreDesc')}</li>
                  <li><strong>{t('guide.exportDesktopSchema')}</strong>{t('guide.exportDesktopSchemaDesc')}</li>
                  <li><strong>{t('guide.exportDesktopFolder')}</strong>{t('guide.exportDesktopFolderDesc')}</li>
                </ul>

                <hr className={styles.divider} />
                <div className={styles.cardTitle}>{t('guide.exportWebTitle')}</div>
                <p className={styles.cardText}>{t('guide.exportWebDesc')}</p>
              </div>
            </div>

            {/* ===== COMMUNITY ===== */}

            {/* Community */}
            <div id="community" className={styles.section}>
              <h2 className={styles.sectionTitle}>{t('guide.community')}</h2>
              <p className={styles.subtitle}>{t('guide.communityDesc')}</p>
              <div className={styles.cardGrid}>
                <div className={styles.card}>
                  <div className={styles.cardTitle}>{t('guide.showroom')}</div>
                  <p className={styles.cardText}>{t('guide.showroomDesc')}</p>
                  <hr className={styles.divider} />
                  <ul className={styles.list}>
                    <li><strong>{t('guide.showroomListings')}</strong>{t('guide.showroomListingsDesc')}</li>
                    <li><strong>{t('guide.showroomPrice')}</strong>{t('guide.showroomPriceDesc')}</li>
                  </ul>
                </div>
                <div className={styles.card}>
                  <div className={styles.cardTitle}>{t('guide.publicProfiles')}</div>
                  <p className={styles.cardText}>{t('guide.publicProfilesDesc')}</p>
                  <hr className={styles.divider} />
                  <ul className={styles.list}>
                    <li><strong>{t('guide.profileAvatar')}</strong>{t('guide.profileAvatarDesc')}</li>
                    <li><strong>{t('guide.profileBio')}</strong>{t('guide.profileBioDesc')}</li>
                    <li><strong>{t('guide.profileLocation')}</strong>{t('guide.profileLocationDesc')}</li>
                    <li><strong>{t('guide.profileSlug')}</strong>{t('guide.profileSlugDesc')}</li>
                  </ul>
                </div>
                <div className={styles.card}>
                  <div className={styles.cardTitle}>{t('guide.wantedList')}</div>
                  <p className={styles.cardText}>{t('guide.wantedListDesc')}</p>
                  <hr className={styles.divider} />
                  <ul className={styles.list}>
                    <li><strong>{t('guide.wantedVisibility')}</strong>{t('guide.wantedVisibilityDesc')}</li>
                    <li><strong>{t('guide.wantedCondition')}</strong>{t('guide.wantedConditionDesc')}</li>
                  </ul>
                </div>
                <div className={styles.card}>
                  <div className={styles.cardTitle}>{t('guide.offersTrading')}</div>
                  <p className={styles.cardText}>{t('guide.offersTradingDesc')}</p>
                  <hr className={styles.divider} />
                  <ul className={styles.list}>
                    <li><strong>{t('guide.offerCreate')}</strong>{t('guide.offerCreateDesc')}</li>
                    <li><strong>{t('guide.offerFlow')}</strong>{t('guide.offerFlowDesc')}</li>
                    <li><strong>{t('guide.offerInbox')}</strong>{t('guide.offerInboxDesc')}</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Direct Messaging */}
            <div id="messaging" className={styles.section}>
              <h2 className={styles.sectionTitle}>{t('guide.directMessaging')}</h2>
              <div className={styles.card}>
                <p className={styles.cardText}>{t('guide.directMessagingDesc')}</p>
                <hr className={styles.divider} />
                <ul className={styles.list}>
                  <li><strong>{t('guide.msgStartConversation')}</strong>{t('guide.msgStartConversationDesc')}</li>
                  <li><strong>{t('guide.msgThreads')}</strong>{t('guide.msgThreadsDesc')}</li>
                  <li><strong>{t('guide.msgUnread')}</strong>{t('guide.msgUnreadDesc')}</li>
                  <li><strong>{t('guide.msgNotifications')}</strong>{t('guide.msgNotificationsDesc')}</li>
                  <li><strong>{t('guide.msgProfileLink')}</strong>{t('guide.msgProfileLinkDesc')}</li>
                </ul>
              </div>
            </div>

            {/* Catalog Sharing */}
            <div id="sharing" className={styles.section}>
              <h2 className={styles.sectionTitle}>{t('guide.catalogSharing')}</h2>
              <div className={styles.card}>
                <p className={styles.cardText}>{t('guide.catalogSharingDesc')}</p>
                <hr className={styles.divider} />
                <ul className={styles.list}>
                  <li><strong>{t('guide.sharingSlug')}</strong>{t('guide.sharingSlugDesc')}</li>
                  <li><strong>{t('guide.sharingPublicAccess')}</strong>{t('guide.sharingPublicAccessDesc')}</li>
                  <li><strong>{t('guide.sharingModal')}</strong>{t('guide.sharingModalDesc')}</li>
                  <li><strong>{t('guide.sharingSocial')}</strong>{t('guide.sharingSocialDesc')}</li>
                  <li><strong>{t('guide.sharingVaultVisibility')}</strong>{t('guide.sharingVaultVisibilityDesc')}</li>
                  <li><strong>{t('guide.sharingVaultTabs')}</strong>{t('guide.sharingVaultTabsDesc')}</li>
                  <li><strong>{t('guide.sharingAnalytics')}</strong>{t('guide.sharingAnalyticsDesc')}</li>
                  <li><strong>{t('guide.sharingMilestones')}</strong>{t('guide.sharingMilestonesDesc')}</li>
                  <li><strong>{t('guide.sharingOg')}</strong>{t('guide.sharingOgDesc')}</li>
                </ul>
              </div>
            </div>

            {/* Favorites */}
            <div id="favorites" className={styles.section}>
              <h2 className={styles.sectionTitle}>{t('guide.favorites')}</h2>
              <div className={styles.card}>
                <p className={styles.cardText}>{t('guide.favoritesDesc')}</p>
              </div>
            </div>

            {/* Activity Feed */}
            <div id="activity" className={styles.section}>
              <h2 className={styles.sectionTitle}>{t('guide.activityFeed')}</h2>
              <div className={styles.card}>
                <p className={styles.cardText}>{t('guide.activityFeedDesc')}</p>
                <hr className={styles.divider} />
                <ul className={styles.list}>
                  <li><strong>{t('guide.activityListed')}</strong>{t('guide.activityListedDesc')}</li>
                  <li><strong>{t('guide.activityTraded')}</strong>{t('guide.activityTradedDesc')}</li>
                  <li><strong>{t('guide.activityJoined')}</strong>{t('guide.activityJoinedDesc')}</li>
                  <li><strong>{t('guide.activityShowcased')}</strong>{t('guide.activityShowcasedDesc')}</li>
                  <li><strong>{t('guide.activityFilters')}</strong>{t('guide.activityFiltersDesc')}</li>
                </ul>
              </div>
            </div>

            {/* Notifications */}
            <div id="notifications" className={styles.section}>
              <h2 className={styles.sectionTitle}>{t('guide.notifications')}</h2>
              <div className={styles.card}>
                <p className={styles.cardText}>{t('guide.notificationsDesc')}</p>
                <hr className={styles.divider} />
                <ul className={styles.list}>
                  <li><strong>{t('guide.notifOffer')}</strong>{t('guide.notifOfferDesc')}</li>
                  <li><strong>{t('guide.notifAccepted')}</strong>{t('guide.notifAcceptedDesc')}</li>
                  <li><strong>{t('guide.notifWanted')}</strong>{t('guide.notifWantedDesc')}</li>
                  <li><strong>{t('guide.notifFavorite')}</strong>{t('guide.notifFavoriteDesc')}</li>
                  <li><strong>{t('guide.notifProfile')}</strong>{t('guide.notifProfileDesc')}</li>
                  <li><strong>{t('guide.notifMilestone')}</strong>{t('guide.notifMilestoneDesc')}</li>
                </ul>
              </div>
            </div>

            {/* ===== ACCOUNT & TIPS ===== */}

            {/* Plans & Pricing */}
            {mode !== 'desktop' && (
              <div id="plans" className={styles.section}>
                <h2 className={styles.sectionTitle}>{t('guide.plans')}</h2>
                <div className={styles.card}>
                  <p className={styles.cardText}>{t('guide.plansDesc')}</p>
                  <hr className={styles.divider} />
                  <div className={styles.cardTitle}>{t('guide.plansFreeTitle')}</div>
                  <ul className={styles.list}>
                    <li>{t('guide.plansFree1')}</li>
                    <li>{t('guide.plansFree2')}</li>
                    <li>{t('guide.plansFree3')}</li>
                    <li>{t('guide.plansFree4')}</li>
                    <li>{t('guide.plansFree5')}</li>
                  </ul>
                  <hr className={styles.divider} />
                  <div className={styles.cardTitle}>{t('guide.plansProTitle')}</div>
                  <ul className={styles.list}>
                    <li>{t('guide.plansPro1')}</li>
                    <li>{t('guide.plansPro2')}</li>
                    <li>{t('guide.plansPro3')}</li>
                    <li>{t('guide.plansPro4')}</li>
                    <li>{t('guide.plansPro5')}</li>
                  </ul>
                  <hr className={styles.divider} />
                  <div className={styles.cardTitle}>{t('guide.plansDesktopTitle')}</div>
                  <ul className={styles.list}>
                    <li>{t('guide.plansDesktop1')}</li>
                    <li>{t('guide.plansDesktop2')}</li>
                    <li>{t('guide.plansDesktop3')}</li>
                    <li>{t('guide.plansDesktop4')}</li>
                  </ul>
                  <hr className={styles.divider} />
                  <p className={styles.cardText}>
                    {t('guide.plansUpgradePre')}
                    <Link to="/download" style={{ color: 'var(--accent)', textDecoration: 'none' }}>{t('guide.plansUpgradeLink')}</Link>
                    {t('guide.plansUpgradePost')}
                  </p>
                </div>
              </div>
            )}

            {/* Admin Panel — admin only */}
            {isAdmin && (
              <div id="admin-panel" className={styles.section}>
                <h2 className={styles.sectionTitle}>{t('guide.adminPanel')}</h2>
                <div className={styles.card}>
                  <p className={styles.cardText}>{t('guide.adminPanelDesc')}</p>
                  <hr className={styles.divider} />
                  <ul className={styles.list}>
                    <li>{t('guide.adminAction1')}</li>
                    <li>{t('guide.adminAction2')}</li>
                    <li>{t('guide.adminAction3')}</li>
                    <li>{t('guide.adminAction4')}</li>
                  </ul>
                </div>
              </div>
            )}

            {/* Tips */}
            <div id="tips" className={styles.section}>
              <h2 className={styles.sectionTitle}>{t('guide.tips')}</h2>
              <div className={styles.card}>
                <ul className={styles.list}>
                  <li>{t('guide.tip1pre')}<span className={styles.kbd}>{t('guide.tip1key')}</span>{t('guide.tip1post')}</li>
                  <li>{t('guide.tip2')}</li>
                  <li>{t('guide.tip3')}</li>
                  <li>{t('guide.tip4')}</li>
                  <li>{t('guide.tip5')}</li>
                  <li>{t('guide.tip6')}</li>
                  <li>{t('guide.tip7')}</li>
                  <li>{t('guide.tip8')}</li>
                  <li>{t('guide.tip9')}</li>
                  <li>{t('guide.tip10')}</li>
                  <li>{t('guide.tip11')}</li>
                  <li>{t('guide.tip12')}</li>
                  <li>{t('guide.tip13')}</li>
                  <li>{t('guide.tip14')}</li>
                  <li>{t('guide.tip15')}</li>
                  <li>{t('guide.tip16')}</li>
                </ul>
              </div>
            </div>

          </div>

          {/* Table of Contents sidebar */}
          <TableOfContents sections={sections} activeId={activeId} />
        </div>
      </div>
    </Shell>
  );
}
